import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import type { MeResponse } from '@adop/shared';
import { AuthContext } from './auth-context';
import type { AuthContextValue } from './auth-context';
import { getSupabase } from './supabase';
import { apiFetch } from './api';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadMe = useCallback(async () => {
    try {
      setError(null);
      const response = await apiFetch<MeResponse>('/api/platform/me');
      setMe(response);
    } catch (cause) {
      setMe(null);
      setError(cause instanceof Error ? cause.message : 'Could not load your account.');
    }
  }, []);

  const refresh = useCallback(async () => {
    const { data } = await getSupabase().auth.getSession();
    setSession(data.session);
    if (data.session) await loadMe();
    else setMe(null);
  }, [loadMe]);

  useEffect(() => {
    let active = true;

    void getSupabase().auth.getSession().then(async ({ data }) => {
      if (!active) return;
        setSession(data.session);
      if (data.session) await loadMe();
      if (active) setLoading(false);
    });

    const { data: subscription } = getSupabase().auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      if (nextSession) {
        void loadMe();
      } else {
        setMe(null);
      }
      setLoading(false);
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, [loadMe]);

  const signInWithGoogle = useCallback(async () => {
    const { error: authError } = await getSupabase().auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    if (authError) throw authError;
  }, []);

  const signOut = useCallback(async () => {
    await getSupabase().auth.signOut();
    setMe(null);
    setSession(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      me,
      profile: me?.profile ?? null,
      memberships: me?.memberships ?? [],
      loading,
      error,
      signInWithGoogle,
      signOut,
      refresh,
    }),
    [session, me, loading, error, signInWithGoogle, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
